import React, { createContext, useContext, useRef } from "react"
import { Link } from "react-router-dom"
import { useMantineTheme, Radio, Spoiler } from "@mantine/core"

import paths from "app/static/frontend/imports/desktopPaths"

import { Icon } from "@/components/Icon"
import { Button } from "@/components/MantineStandard"
import { escapeRegExp } from "@/helpers/regex"

import { Loading } from "@/components/TopNavigation/components/RegionSelector/Dropdown/Options/Loading/Loading"
import { Placeholder } from "@/components/TopNavigation/components/RegionSelector/Dropdown/Options/Placeholder/Placeholder"
import { useStyles } from "@/components/TopNavigation/components/RegionSelector/Dropdown/Options/Options.styles"
import { RegionSelectorContext } from "@/components/TopNavigation/components/RegionSelector/RegionSelectorContext"
import { Region, RegionGroup } from "@/components/TopNavigation/components/RegionSelector/types"
import {
    createCustomRegionsDict,
    createRegionsDict,
    handleApplyRegion,
} from "@/components/TopNavigation/components/RegionSelector/Dropdown/helpers"

const RadioGroup = ({ children }) => {
    const { selectedRegionValue, setSelectedRegionValue } = useContext(RegionSelectorContext)
    const { classes } = useStyles({})

    return (
        <Radio.Group
            value={selectedRegionValue}
            onChange={setSelectedRegionValue}
            wrapperProps={{ role: "radiogroup", "aria-label": "Choose a region" }}
            className={classes.groupContainer}
        >
            {children}
        </Radio.Group>
    )
}

const Option = ({
    description,
    disabled,
    label,
    value,
}: {
    description?: string
    disabled?: boolean
    label: string
    value: string
}) => {
    // This is a design-request adjustment for region descriptions with a single word in the second line
    // (ex. "you" for the "US States & Local" region). Removing this padding allows the single word to stay on the first line.
    const shouldReducePadding = label === DjangIO.app.models.SupportedRegion.states_and_local.label

    const { classes } = useStyles({ shouldReducePadding })
    const descriptionId = `${value}-description`.replace(/ /g, "+")

    return (
        <Radio
            {...(description && { "aria-describedby": descriptionId })}
            className={classes.option}
            disabled={disabled}
            label={
                <>
                    <p>{label}</p>
                    <div className={classes.description} id={descriptionId}>
                        <Spoiler
                            maxHeight={30}
                            showLabel={
                                <>
                                    More
                                    <Icon icon="angle-down" />
                                </>
                            }
                            hideLabel={
                                <>
                                    Less
                                    <Icon icon="angle-up" />
                                </>
                            }
                        >
                            {description}
                        </Spoiler>
                    </div>
                </>
            }
            onClick={() => handleApplyRegion(String(value))}
            value={String(value)}
        />
    )
}

const Groups = () => {
    const { regionGroups } = useContext(RegionSelectorContext)
    const { searchRegex } = useContext(OptionsContext)
    const { classes } = useStyles({})

    const filteredRegionGroups = regionGroups.map((regionGroup: RegionGroup) => ({
        ...regionGroup,
        regions: regionGroup.regions.filter((region) => searchRegex.test(region.label)),
    }))

    if (filteredRegionGroups.every((regionGroup: RegionGroup) => regionGroup.regions.length === 0)) {
        return <Placeholder description="Rephrase your search and try again" heading="No regions found" />
    }

    const renderOption = (region: Region) => {
        if (region.disabled && region.isMajor) {
            return (
                <div className={classes.majorRegionDisabled} key={region.value}>
                    <div className="major-region-disabled--title">{region.label}</div>
                    <p className="major-region-disabled--description">
                        {region.description}{" "}
                        <a href={region.productPagePath} target="_blank" rel="noreferrer">
                            Learn more
                        </a>
                        .
                    </p>
                    <Icon icon="lock" className="major-region-disabled--icon" />
                </div>
            )
        }

        return (
            <Option
                description={region.description}
                disabled={region.disabled}
                label={region.label}
                key={region.value}
                value={String(region.value)}
            />
        )
    }

    return (
        <RadioGroup>
            {filteredRegionGroups.map((regionGroup: RegionGroup) =>
                regionGroup.regions.length > 0 ? (
                    <React.Fragment key={regionGroup.key}>
                        <div className={classes.groupTitle}>{regionGroup.groupLabel}</div>
                        {regionGroup.regions.map(renderOption)}
                    </React.Fragment>
                ) : null,
            )}
        </RadioGroup>
    )
}

const Custom = () => {
    const { customRegions, toggleIsOpen } = useContext(RegionSelectorContext)
    const { searchRegex } = useContext(OptionsContext)
    const { classes } = useStyles({})

    if (customRegions.isLoading) return <Loading />

    if (!customRegions.data?.length) {
        const newRegionLink = (
            <Button
                component={Link}
                leftIcon={<Icon icon="plus" />}
                onClick={toggleIsOpen}
                size="xs"
                to={paths.customRegionFormPrefix}
                variant="outline"
            >
                New custom region
            </Button>
        )

        return (
            <Placeholder
                actions={newRegionLink}
                description="Seems like you don't have any custom region under your account. Start creating your first one below."
                heading="Start your first region"
                renderIcon={(className) => <Placeholder.StartIcon className={className} />}
            />
        )
    }

    const filteredCustomRegions = customRegions.data.filter((region) => searchRegex.test(region.label))

    const manageCustomRegionLink = (
        <Button
            className={classes.link}
            component={Link}
            onClick={toggleIsOpen}
            rightIcon={<Icon className={classes.pen} icon="pen" />}
            size="xs"
            to={paths.customRegionManagement}
            variant="outline"
        >
            Manage custom regions
        </Button>
    )

    if (!filteredCustomRegions.length) {
        return (
            <Placeholder
                actions={manageCustomRegionLink}
                description="Try searching for a different term or go to Custom Regions management"
            />
        )
    }

    return (
        <>
            <RadioGroup>
                {filteredCustomRegions.map((customRegion) => (
                    <Option
                        description={customRegion.description}
                        label={customRegion.label}
                        key={customRegion.value}
                        value={customRegion.value}
                    />
                ))}
            </RadioGroup>
            <div className={classes.linkWrapper}>{manageCustomRegionLink}</div>
        </>
    )
}

const Recent = () => {
    const { recentRegions, customRegions, regionGroups } = useContext(RegionSelectorContext)
    const { searchRegex } = useContext(OptionsContext)
    const theme = useMantineTheme()

    const regionsDict = createRegionsDict(regionGroups)
    const customRegionsDict = createCustomRegionsDict(customRegions)

    const filteredRecentRegions = recentRegions.data?.filter((region) =>
        searchRegex.test(regionsDict[region.description]?.label || customRegionsDict[region.description]?.label),
    )

    const renderOption = (regionAbbrev: string) => {
        const region = regionsDict[regionAbbrev] || customRegionsDict[regionAbbrev]

        if (!region) return null

        return (
            <Option
                description={region.description}
                disabled={region.disabled}
                label={region.label}
                key={region.value}
                value={String(region.value)}
            />
        )
    }

    if (customRegions.isLoading) return <Loading />

    return (
        <>
            {filteredRecentRegions?.length > 0 ? (
                <RadioGroup>{filteredRecentRegions.map((item) => renderOption(item.description))}</RadioGroup>
            ) : (
                <Placeholder
                    description="Start choosing regions to populate this view"
                    heading="List is empty"
                    renderIcon={(className) => (
                        <Icon icon={"history"} className={className} color={theme.colors.violet[5]} />
                    )}
                />
            )}
        </>
    )
}

const OptionsContext = createContext({ searchRegex: new RegExp("", "i") })

export const Options = ({ children, search }) => {
    const { classes } = useStyles({})
    const searchRegex = new RegExp(escapeRegExp(search), "i")

    return (
        <OptionsContext.Provider value={{ searchRegex }}>
            <div className={classes.options}>{children}</div>
        </OptionsContext.Provider>
    )
}

Options.Groups = Groups
Options.Custom = Custom
Options.Recent = Recent
